import React, { useState, useEffect, FC } from 'react';
import SectionTitle from '@src/components/SectionTitle';
import apolloClient from '@src/graphql/apollo';
import Checkbox from '@src/components/Checkbox';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { idify } from '@src/util/idify';
import { GetRelocationBenefitsDocument, GetRelocationBenefitsQuery } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import { SetRelocationBenefits } from './interface/setRelocationBenefits.interface';
import { OnChange } from './interface/onChange.interface';
import { GenerateColumn } from './interface/generateColumn.interface';
import { Props } from './props';
import './styles.scss';

export const RelocationPackage: FC<Props> = ({ opening, updateRelocationBenefits }) => {
  const [openingBenefits, setOpeningBenefits] = useState(opening.relocationBenefits || []);
  const [benefits, setBenefits] = useState<GetRelocationBenefitsQuery['relocationBenefits']>([]);

  const fetchBenefits = async () => {
    const { data } = await apolloClient.query<GetRelocationBenefitsQuery>({
      query: GetRelocationBenefitsDocument,
    });

    setBenefits(data.relocationBenefits);
  };

  const setRelocationBenefits: SetRelocationBenefits = supportBenefits => {
    // Update local state
    setOpeningBenefits(supportBenefits);

    // Send request to update relocation benefits
    updateRelocationBenefits(supportBenefits);

    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.SET_RELOCATION_PACKAGES,
      payload: {
        'Opening Id': idify(opening),
        'Relocation Packages': supportBenefits,
      },
    });
  };

  const onChange: OnChange = benefit => {
    let supportBenefits = [...openingBenefits];
    const isBenefitSelected = supportBenefits.find(({ id: benefitId }) => benefitId === benefit.id);

    if (!isBenefitSelected) {
      supportBenefits.push(benefit);
    } else {
      supportBenefits = supportBenefits.filter(({ id: benefitId }) => benefitId !== benefit.id);
    }

    return setRelocationBenefits(supportBenefits);
  };

  const generateColumn: GenerateColumn = (list, startIndex, endIndex) => {
    if (!Array.isArray(list)) {
      return null;
    }

    return (
      <div className="benefits-wrapper__column">
        {list.map((benefit, index) => {
          const { id, value } = benefit;

          if (index >= startIndex && index < endIndex) {
            const isChecked = openingBenefits && !!openingBenefits.find(({ id: benefitId }) => benefitId === id);

            return <Checkbox key={id} label={value} onChange={() => onChange(benefit)} value={isChecked} />;
          }

          return null;
        })}
      </div>
    );
  };

  useEffect(() => {
    fetchBenefits();
  }, []);

  if (!benefits) {
    return null;
  }

  return (
    <div className="page-section">
      <SectionTitle size="M" subtitle="Choose one or many" title="Relocation Package" />

      <div className="benefits-wrapper">
        {generateColumn(benefits, 0, 4)}
        {generateColumn(benefits, 4, 8)}
      </div>
    </div>
  );
};
